
import type { IWidgetView, OrderType } from '@wscsports/blaze-web-sdk';
import type { WidgetConfig, BlazeWidgetAPIResponse } from './types';
import blazeSDKManager from './blaze-sdk-manager';
import { 
  buildSDKConfig, 
  convertAPIResponseToConfig, 
  determineWidgetType as mapDetermineWidgetType,
  buildLabelsDataSource
} from './config-mapper';

/**
 * Widget Manager - Manages Blaze SDK widget lifecycle
 * Handles widget creation, destruction, and updates
 */
class WidgetManager {
  private widgets: Map<string, IWidgetView> = new Map();

  private convertAPIResponseToConfig(apiResponse: BlazeWidgetAPIResponse): WidgetConfig {
    return convertAPIResponseToConfig(apiResponse);
  }

  private prepareConfig(config: WidgetConfig | BlazeWidgetAPIResponse): WidgetConfig {
    const labels = (config as any)?.labels;
    const searchText = (config as any)?.searchText;
    const orderBy = (config as any)?.orderBy;
    
    let widgetConfig: WidgetConfig = 'type' in config && config.type === 'blaze' 
      ? { 
          ...(config as any), 
          ...this.convertAPIResponseToConfig(config as BlazeWidgetAPIResponse)
        }
      : config as WidgetConfig;
    
    return this.applyLabels(widgetConfig, labels, searchText, orderBy);
  }

  private applyLabels(widgetConfig: any, labelsProp?: string, searchText?: string, orderBy?: string): any {
    if (!widgetConfig) {
      return widgetConfig;
    }

    if (labelsProp) {
      try {
        const labelsData: { isAndGroup: boolean, labels: string[] } = JSON.parse(labelsProp);
          
        if (!labelsData || !Array.isArray(labelsData.labels) || labelsData.labels.length === 0) {
          return this.applySearchTextOnly(widgetConfig, searchText, orderBy);
        }

        const existingSelector = widgetConfig?.selector;
        let labelGroups;
        
        if (labelsData?.isAndGroup) {
          labelGroups = [{
            id: 'dynamic-and-group',
            andLabels: labelsData.labels
          }];
        } else {
          labelGroups = labelsData.labels.map((label: string, index: number) => ({
            id: `dynamic-or-${index}`,
            andLabels: [label]
          }));
        }

        return {
          ...widgetConfig,
          ...(searchText ? { searchText } : {}),
          ...(orderBy ? { orderBy } : {}),
          selector: {
            source: 'labels',
            sortBy: orderBy || existingSelector?.sortBy,
            labels: {
              labelGroups: labelGroups
            }
          }
        };
      } catch (error) {
        console.error('Error applying labels:', error);
        return this.applySearchTextOnly(widgetConfig, searchText, orderBy);
      }
    }

    return this.applySearchTextOnly(widgetConfig, searchText, orderBy);
  }

  private applySearchTextOnly(widgetConfig: any, searchText?: string, orderBy?: string): any {
    if (!searchText) {
      return widgetConfig;
    }

    const existingSelector = widgetConfig?.selector;

    return {
      ...widgetConfig,
      searchText,
      orderBy,
      selector: {
        source: 'text',
        text: searchText,
        sortBy: orderBy || existingSelector?.sortBy
      }
    };
  }

  async createWidget(config: WidgetConfig | BlazeWidgetAPIResponse): Promise<IWidgetView> {
    const widgetConfig = this.prepareConfig(config);
    
    if (this.widgets.has(widgetConfig?.containerId)) {
      this.destroyWidget(widgetConfig.containerId);
    }
    
    if (!blazeSDKManager.isReady()) {
      const shouldSetGeo = Boolean(widgetConfig?.isGeoBased);
      const geoLocation = shouldSetGeo ? widgetConfig?.viewerCountry : undefined;
      const layoutDirection = widgetConfig?.style?.layoutDirection;
      await blazeSDKManager.initialize(widgetConfig.apiKey, {
        ...(shouldSetGeo && geoLocation ? { geoLocation } : {}),
        ...(layoutDirection ? { layoutDirection } : {}),
      } as any);
    }
    
    const sdk = blazeSDKManager.getSDK();
    
    if (!sdk) {
      throw new Error('Blaze SDK not initialized');
    }

    const sdkConfig = buildSDKConfig(widgetConfig, {
      eventHandlers: widgetConfig.eventHandlers
    });
    
    const widgetType = mapDetermineWidgetType(widgetConfig.layout);
    
    let widget: IWidgetView;
    
    switch (widgetType) {
      case 'WidgetGridView':
        widget = sdk.WidgetGridView(widgetConfig.containerId, sdkConfig);
        break;
      case 'WidgetRowView':
        widget = sdk.WidgetRowView(widgetConfig.containerId, sdkConfig);
        break;
      case 'WidgetEmbeddedStory':
        widget = sdk.WidgetEmbeddedStory(widgetConfig.containerId, sdkConfig);
        break;
      default:
        throw new Error(`Unsupported widget type: ${widgetType}`);
    }

    // Store widget instance
    this.widgets.set(widgetConfig.containerId, widget);
    
    return widget;
  }

  destroyWidget(containerId: string): void {
    const widget = this.widgets.get(containerId);
    if (widget) {
      try {
        if (widget && typeof widget.remove === 'function' && widget.parentElement) {
          widget.remove();
        }
      } finally {
        this.widgets.delete(containerId);
      }
    }
  }
}

const widgetManager = new WidgetManager();
export default widgetManager; 